import { useMemo, useRef, CSSProperties } from 'react';
import { ChatWidgetTheme, ChatWidgetStyles } from '../types';
import { defaultTheme } from '../themes/defaultTheme';

/**
 * Tipos de retorno do hook useTheme
 */
export interface ProcessedTheme {
  // Valores processados do tema
  theme: Required<ChatWidgetTheme>;

  // Estilos CSS gerados
  styles: {
    container: CSSProperties;
    windowContainer: CSSProperties;
    toggleButton: CSSProperties;
    header: CSSProperties;
    headerTitle: CSSProperties;
    headerSubtitle: CSSProperties;
    headerIcon: CSSProperties;
    messagesContainer: CSSProperties;
    messageUser: CSSProperties;
    messageBot: CSSProperties;
    messageContent: CSSProperties;
    messageTimestamp: CSSProperties;
    inputContainer: CSSProperties;
    input: CSSProperties;
    sendButton: CSSProperties;
    sendButtonDisabled: CSSProperties;
    loadingIndicator: CSSProperties;
    errorMessage: CSSProperties;
  };
}

/**
 * Função para fazer deep merge de objetos
 */
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target } as any;

  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(result[key] || {}, source[key] as any);
    } else if (source[key] !== undefined) {
      result[key] = source[key];
    }
  }

  return result;
}

/**
 * Hook para processar tema e gerar estilos CSS
 *
 * Faz merge de: defaultTheme → theme → customStyles
 */
export const useTheme = (
  theme?: ChatWidgetTheme,
  customStyles?: ChatWidgetStyles
): ProcessedTheme => {
  // Usa JSON.stringify para comparação estável de objetos
  const themeKey = useMemo(() => JSON.stringify(theme), [theme]);
  const customStylesKey = useMemo(() => JSON.stringify(customStyles), [customStyles]);

  return useMemo(() => {
    // Merge tema customizado com tema padrão
    const mergedTheme = deepMerge(defaultTheme, theme || {}) as Required<ChatWidgetTheme>;

    // Gerar estilos CSS a partir do tema
    const { colors, layout, typography, general } = mergedTheme;

    const styles: ProcessedTheme['styles'] = {
      // Container principal
      container: {
        display: 'flex',
        flexDirection: 'column',
        width: layout.windowWidth,
        height: layout.windowHeight,
        borderRadius: layout.borderRadius,
        overflow: 'hidden',
        backgroundColor: colors.lightBackground,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        fontFamily: general.fontFamily,
        transition: `all ${layout.transitionDuration}`,
        ...customStyles?.container,
      },

      // Container do widget em mode window
      windowContainer: {
        position: 'fixed' as const,
        bottom: 'max(90px, env(safe-area-inset-bottom, 90px))',
        right: 'max(20px, env(safe-area-inset-right, 20px))',
        display: 'flex',
        flexDirection: 'column' as const,
        width: layout.windowWidth,
        height: layout.windowHeight,
        borderRadius: layout.borderRadius,
        overflow: 'hidden',
        backgroundColor: colors.lightBackground,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        fontFamily: general.fontFamily,
        zIndex: 9999,
        transition: `all ${layout.transitionDuration}`,
        ...customStyles?.container,
      },

      // Botão toggle flutuante (mode window)
      toggleButton: {
        position: 'fixed' as const,
        bottom: 'max(20px, env(safe-area-inset-bottom, 20px))',
        right: 'max(20px, env(safe-area-inset-right, 20px))',
        width: layout.toggleSize,
        height: layout.toggleSize,
        borderRadius: layout.toggleBorderRadius,
        backgroundColor: colors.toggleBackground,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.toggleIconStroke,
        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.4)',
        transition: `all ${layout.transitionDuration}`,
        zIndex: 9998,
      },

      // Header
      header: {
        padding: layout.headerPadding,
        height: layout.headerHeight,
        backgroundColor: colors.headerBackground,
        color: colors.headerText,
        display: 'flex',
        alignItems: 'center',
        gap: layout.spacing,
        ...customStyles?.header,
      },

      // Título do header
      headerTitle: {
        margin: 0,
        fontSize: typography.titleFontSize,
        fontWeight: 600,
        color: colors.headerText,
        ...customStyles?.headerTitle,
      },

      // Subtítulo do header
      headerSubtitle: {
        margin: 0,
        fontSize: typography.subtitleFontSize,
        lineHeight: typography.subtitleLineHeight,
        color: colors.headerText,
        opacity: 0.9,
      },

      // Ícone do header
      headerIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: general.iconSize,
        height: general.iconSize,
        fontSize: general.iconSize,
        padding: general.iconPadding,
      },

      // Container de mensagens
      messagesContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: layout.spacing,
        display: 'flex',
        flexDirection: 'column',
        gap: layout.spacing,
        backgroundColor: colors.lightBackground,
        ...customStyles?.messagesContainer,
      },

      // Mensagem do usuário
      messageUser: {
        alignSelf: 'flex-end',
        maxWidth: 'min(75%, 280px)',
        padding: layout.messagePadding,
        borderRadius: layout.messageBorderRadius,
        backgroundColor: colors.messageUserBackground,
        color: colors.messageUserText,
        wordWrap: 'break-word',
        ...customStyles?.messageUser,
      },

      // Mensagem do bot
      messageBot: {
        alignSelf: 'flex-start',
        maxWidth: 'min(75%, 280px)',
        padding: layout.messagePadding,
        borderRadius: layout.messageBorderRadius,
        backgroundColor: colors.messageBotBackground,
        color: colors.messageBotText,
        wordWrap: 'break-word',
        border: `1px solid ${colors.lightShade100}`,
        ...customStyles?.messageBot,
      },

      // Conteúdo da mensagem
      messageContent: {
        margin: 0,
        fontSize: typography.messageFontSize,
        lineHeight: typography.messageLineHeight,
        ...customStyles?.messageContent,
      },

      // Timestamp
      messageTimestamp: {
        fontSize: '0.75rem',
        marginTop: '4px',
        opacity: 0.7,
        ...customStyles?.messageTimestamp,
      },

      // Container do input
      inputContainer: {
        display: 'flex',
        padding: layout.spacing,
        gap: layout.spacing,
        backgroundColor: colors.inputBackground,
        borderTop: `1px solid ${colors.lightShade100}`,
        ...customStyles?.inputContainer,
      },

      // Input
      input: {
        flex: 1,
        height: layout.textareaHeight,
        padding: '0 1rem',
        border: `1px solid ${colors.inputBorder}`,
        borderRadius: layout.borderRadius,
        fontSize: typography.messageFontSize,
        fontFamily: general.fontFamily,
        outline: 'none',
        backgroundColor: colors.inputBackground,
        transition: `border-color ${layout.transitionDuration}`,
        ...customStyles?.input,
      },

      // Botão de enviar
      sendButton: {
        width: layout.textareaHeight,
        height: layout.textareaHeight,
        padding: 0,
        backgroundColor: colors.buttonBackground,
        color: colors.buttonText,
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: `all ${layout.transitionDuration}`,
        flexShrink: 0,
        ...customStyles?.sendButton,
      },

      // Botão desabilitado
      sendButtonDisabled: {
        backgroundColor: colors.disabledText,
        cursor: 'not-allowed',
        opacity: 0.6,
        ...customStyles?.sendButtonDisabled,
      },

      // Indicador de carregamento
      loadingIndicator: {
        textAlign: 'center',
        padding: layout.spacing,
        color: colors.typingIndicator,
        fontSize: '0.875rem',
        fontStyle: 'italic',
        ...customStyles?.loadingIndicator,
      },

      // Mensagem de erro
      errorMessage: {
        padding: layout.spacing,
        color: '#dc3545',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: layout.borderRadius,
        fontSize: '0.875rem',
        textAlign: 'center',
        margin: `0 ${layout.spacing}`,
        ...customStyles?.errorMessage,
      },
    };

    return {
      theme: mergedTheme,
      styles,
    };
  }, [themeKey, customStylesKey]);
};
